<script setup>
import { ref, reactive, watch, onMounted, computed } from 'vue';
import { Inertia } from '@inertiajs/inertia';
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';
import TabView from 'primevue/tabview';
import TabPanel from 'primevue/tabpanel';
import axios from 'axios';
import { useToast } from 'primevue/usetoast';
import Calendar from 'primevue/calendar';
import Dropdown from 'primevue/dropdown';
import Textarea from 'primevue/textarea';
import Checkbox from 'primevue/checkbox';
import InputText from 'primevue/inputtext';
import QuickAddCustomerModal from './QuickAddCustomerModal.vue';

const toast = useToast();

const props = defineProps({
  modelValue: { type: Boolean, required: true },
  selectedSlot: { type: Object, default: null },
  serviceOptions: { type: Array, default: () => [] },
  providers: { type: Array, default: () => [] },
  noteTypes: { type: Array, default: () => [] },
});

const emit = defineEmits(['update:modelValue', 'created']);

const visible = ref(props.modelValue);
watch(() => props.modelValue, (v) => (visible.value = v));
watch(visible, (v) => emit('update:modelValue', v));

const localForm = reactive({
  customerQuery: '',
  customerId: '',
  customerGender: null,
  serviceUuid: '',
  providerUuid: '',
  startDatetime: null,
  endDatetime: null,
  customerNotes: '',
  notesInternal: '',
  resourceId: null,
  isWalkInBooking: false,
});

const noteForm = reactive({
  note: '',
  typeUuid: '',
  providerUuid: '',
  start: null,
  end: null,
  isBlocked: false,
});

const blockForm = reactive({
  title: '',
  notes: '',
  providerUuid: '',
  date: null,
  startTime: null,
  endTime: null,
  isRecurring: false,
  endDate: null,
  daysOfWeek: [],
});

const activeMainTab = ref(0); // 0: Booking, 1: Note
const serviceCategories = ref([]);
const noteTypes = computed(() => props.noteTypes);
const allProviders = computed(() => props.providers);

const loadingData = reactive({
  categories: false,
  providers: false,
  noteTypes: false,
});

const resetForms = () => {
  // Reset Booking Form
  localForm.customerQuery = '';
  localForm.customerId = '';
  localForm.serviceUuid = '';
  localForm.providerUuid = '';
  localForm.startDatetime = null;
  localForm.endDatetime = null;
  localForm.customerNotes = '';
  localForm.notesInternal = '';
  localForm.resourceId = null;
  
  // Reset Note Form
  noteForm.note = '';
  noteForm.typeUuid = props.noteTypes?.length > 0 ? props.noteTypes[0].uuid : '';
  noteForm.providerUuid = '';
  noteForm.start = null;
  noteForm.end = null;
  noteForm.isBlocked = false;

  // Reset Block Form
  blockForm.title = '';
  blockForm.notes = '';
  blockForm.providerUuid = '';
  blockForm.date = null;
  blockForm.startTime = null;
  blockForm.endTime = null;
  blockForm.isRecurring = false;
  blockForm.endDate = null;
  blockForm.daysOfWeek = [];
  
  customerOptions.value = [];
  activeMainTab.value = 0;
};

watch(() => props.noteTypes, (newTypes) => {
  if (!noteForm.typeUuid && newTypes?.length > 0) {
    noteForm.typeUuid = newTypes[0].uuid;
  }
}, { immediate: true });

watch(visible, (v) => {
  if (!v) {
    resetForms();
  }
});

const customerOptions = ref([]);
const searchingCustomers = ref(false);
const loading = ref(false);

const searchCustomers = async () => {
  if (!localForm.customerQuery?.trim()) {
    customerOptions.value = [];
    return;
  }

  searchingCustomers.value = true;
  try {
    const { data } = await axios.get('/ajax/customers', { params: { s: localForm.customerQuery.trim() } });
    const rows = data?.customers?.data || [];
    customerOptions.value = rows.map((row) => ({ 
        id: row.id, 
        label: [row.first_name, row.last_name].filter(Boolean).join(' ').trim() || row.email, 
        email: row.email,
        gender: row.gender
    }));
  } catch (e) {
    customerOptions.value = [];
  } finally {
    searchingCustomers.value = false;
  }
};

const chooseCustomer = (option) => {
  localForm.customerId = String(option.id);
  localForm.customerGender = option.gender;
  localForm.customerQuery = `${option.label}${option.email ? ` (${option.email})` : ''}`;
  customerOptions.value = [];
};

const slotChecked = ref(false);

const loadServices = async (providerUuid = null) => {
  try {
    const params = { all: 1 };
    if (providerUuid) {
      params.provider_id = providerUuid;
    }
    if (localForm.customerGender) {
      params.resource_type_filter = localForm.customerGender;
    }
    const { data } = await axios.get('/ajax/admin/services', { params });
    const rows = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
    serviceOptionsLocal.value = rows.map((s) => ({
      uuid: s.uuid,
      name: s.name,
      duration: s.duration || 0,
      buffer_before: s.buffer_before_minutes || 0,
      buffer_after: s.buffer_after_minutes || 0,
      device_requirements: s.device_requirements || [],
    }));
  } catch (e) {
    serviceOptionsLocal.value = [];
  }
};

watch(() => localForm.providerUuid, (newUuid) => {
  localForm.serviceUuid = ''; // Reset service selection when provider changes
  if (newUuid) {
    loadServices(newUuid);
  } else {
    serviceOptionsLocal.value = [];
  }
});

watch(() => localForm.customerId, (newId) => {
  if (localForm.providerUuid) {
    loadServices(localForm.providerUuid);
  }
});

const loadServiceCategories = async () => {
  loadingData.categories = true;
  try {
    const { data } = await axios.get('/ajax/admin/service-categories', { params: { all: 1 } });
    serviceCategories.value = (data?.data || data || []).map(c => ({ id: c.id, uuid: c.uuid, name: c.name }));
  } catch (e) {
    serviceCategories.value = [];
  } finally {
    loadingData.categories = false;
  }
};

// Removed redundant fetchers - using props instead

const serviceOptionsLocal = ref(props.serviceOptions || []);

watch(
  () => props.serviceOptions,
  (next) => {
    if (Array.isArray(next) && next.length) {
      serviceOptionsLocal.value = next;
    }
  },
  { deep: true }
);

const isSelectedStartInSlots = (selectedStart, slots) => {
  if (!selectedStart || !Array.isArray(slots)) return false;

  // Normalize to local 'YYYY-MM-DDTHH:MM' - avoids UTC vs server-local mismatch
  const normalizeLocal = (value) => {
    const d = value instanceof Date ? value : new Date(String(value).replace(' ', 'T'));
    if (isNaN(d.getTime())) return '';
    const yr = d.getFullYear();
    const mo = String(d.getMonth() + 1).padStart(2, '0');
    const dy = String(d.getDate()).padStart(2, '0');
    const hr = String(d.getHours()).padStart(2, '0');
    const mn = String(d.getMinutes()).padStart(2, '0');
    return yr + '-' + mo + '-' + dy + 'T' + hr + ':' + mn;
  };
  const target = normalizeLocal(selectedStart);
  return slots.some((slot) => slot?.available && normalizeLocal(slot.start) === target);
};

const formatDate = (date) => {
  if (!date) return null;
  const d = new Date(date);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};

const formatTime = (date) => {
  if (!date) return '--:--';
  if (typeof date === 'string' && date.includes(' ')) {
    return date.split(' ')[1].substring(0, 5);
  }
  const d = date instanceof Date ? date : new Date(date);
  return d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
};

const selectedService = computed(() => {
  if (!localForm.serviceUuid) return null;
  return serviceOptionsLocal.value.find(s => s.uuid === localForm.serviceUuid) || null;
});

const hasDeviceSelection = computed(() => {
  return selectedService.value?.device_requirements?.some(r => r.device?.allow_resource_selection) || false;
});

const availableResources = ref([]);
const loadingResources = ref(false);

const fetchAvailableResources = async () => {
  if (!localForm.serviceUuid || !localForm.startDatetime || !localForm.endDatetime || !hasDeviceSelection.value) {
    availableResources.value = [];
    localForm.resourceId = null;
    return;
  }
  
  loadingResources.value = true;
  try {
    const d = new Date(localForm.startDatetime);
    const pad = (n) => String(n).padStart(2, '0');
    const isoStart = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
    
    const dEnd = new Date(localForm.endDatetime);
    const isoEnd = `${dEnd.getFullYear()}-${pad(dEnd.getMonth() + 1)}-${pad(dEnd.getDate())} ${pad(dEnd.getHours())}:${pad(dEnd.getMinutes())}:${pad(dEnd.getSeconds())}`;
    
    const { data } = await axios.get(`/ajax/admin/services/${localForm.serviceUuid}/available-resources`, {
        params: { start_datetime: isoStart, end_datetime: isoEnd }
    });
    const allResources = Array.isArray(data) ? data : (data.data || []);
    availableResources.value = allResources.filter(r => r.available !== false);
    
    if (availableResources.value.length === 1 && availableResources.value[0].available && !localForm.resourceId) {
       localForm.resourceId = availableResources.value[0].resource_id;
    }
  } catch (e) {
    availableResources.value = [];
    console.error('Failed to fetch available resources', e);
  } finally {
    loadingResources.value = false;
  }
};

watch([() => localForm.serviceUuid, () => localForm.startDatetime, () => localForm.endDatetime, () => hasDeviceSelection.value], fetchAvailableResources);

// Total duration including buffers (for blocked time = what gets blocked on the calendar)
const totalBlockedMinutes = computed(() => {
  const s = selectedService.value;
  if (!s) return 0;
  return (s.buffer_before || 0) + (s.duration || 0) + (s.buffer_after || 0);
});

// blocked_start = start - buffer_before
const blockedStartDatetime = computed(() => {
  if (!localForm.startDatetime || !selectedService.value) return null;
  const bufferBefore = selectedService.value.buffer_before || 0;
  const d = new Date(localForm.startDatetime);
  return new Date(d.getTime() - bufferBefore * 60000);
});

const actualDurationMinutes = computed(() => {
  if (localForm.startDatetime && localForm.endDatetime) {
    const start = new Date(localForm.startDatetime);
    const end = new Date(localForm.endDatetime);
    if (end > start) {
      return (end.getTime() - start.getTime()) / 60000;
    }
  }
  
  const s = selectedService.value;
  let duration = s?.duration || 0;
  if (duration === 0 && props.selectedSlot?.start && props.selectedSlot?.end) {
    const ss = new Date(props.selectedSlot.start);
    const se = new Date(props.selectedSlot.end);
    duration = (se.getTime() - ss.getTime()) / 60000;
  }
  return duration;
});

// blocked_end = start + actualDuration + buffer_after
const calculatedEndDatetime = computed(() => {
  if (!localForm.startDatetime) return props.selectedSlot?.end || '';

  const start = new Date(localForm.startDatetime);
  const s = selectedService.value;
  let bufferAfter = s?.buffer_after || 0;

  // blocked_end = start + actualDuration + buffer_after
  const blockedEnd = new Date(start.getTime() + (actualDurationMinutes.value + bufferAfter) * 60000);
  return formatDate(blockedEnd);
});

const durationBreakdown = computed(() => {
  const s = selectedService.value;
  if (!s) return null;
  const parts = [];
  if (s.buffer_before > 0) parts.push(`${s.buffer_before}m Puffer vorher`);
  parts.push(`${actualDurationMinutes.value}m Service`);
  if (s.buffer_after > 0) parts.push(`${s.buffer_after}m Puffer nachher`);
  const total = (s.buffer_before || 0) + actualDurationMinutes.value + (s.buffer_after || 0);
  return { parts, total };
});

watch([() => localForm.startDatetime, () => localForm.serviceUuid], () => {
    if (!localForm.startDatetime) {
        localForm.endDatetime = null;
        return;
    }
    const start = new Date(localForm.startDatetime);
    const s = selectedService.value;
    let duration = s?.duration || 0;
    if (duration === 0 && props.selectedSlot?.start && props.selectedSlot?.end) {
        const ss = new Date(props.selectedSlot.start);
        const se = new Date(props.selectedSlot.end);
        duration = (se.getTime() - ss.getTime()) / 60000;
    }
    
    localForm.endDatetime = new Date(start.getTime() + duration * 60000);
});

const precheckSlot = async () => {
  slotChecked.value = false;

  if (!localForm.startDatetime || !localForm.providerUuid || !localForm.serviceUuid) {
    return false;
  }

  try {
    // Extract local date string without UTC conversion to avoid midnight date-shift
    const _sd = localForm.startDatetime instanceof Date ? localForm.startDatetime : new Date(localForm.startDatetime);
    const datePart = _sd.getFullYear() + '-' + String(_sd.getMonth() + 1).padStart(2, '0') + '-' + String(_sd.getDate()).padStart(2, '0');
    const { data } = await axios.get('/ajax/admin/bookings/available-slots', {
      params: {
        service_id: localForm.serviceUuid,
        provider_id: localForm.providerUuid,
        date: datePart,
        step_minutes: 15,
        duration_minutes: actualDurationMinutes.value,
      },
    });

    const rows = Array.isArray(data?.data) ? data.data : [];
    const ok = isSelectedStartInSlots(localForm.startDatetime, rows);
    slotChecked.value = true;

    if (!ok) {
      toast.add({ severity: 'warn', summary: 'Nicht verfügbar', detail: 'Der gewählte Slot ist für diesen Service nicht verfügbar.', life: 5000 });
      return false;
    }

    return true;
  } catch (e) {
    toast.add({ severity: 'error', summary: 'Fehler', detail: 'Slot-Prüfung fehlgeschlagen. Bitte erneut versuchen.', life: 5000 });
    return false;
  }
};

watch(() => localForm.serviceUuid, () => {
  slotChecked.value = false;
});

onMounted(async () => {
  await loadServiceCategories();
  
  if (props.serviceOptions?.length) {
    serviceOptionsLocal.value = props.serviceOptions;
  } else {
    loadServices();
  }
});

watch(() => props.selectedSlot, (slot) => {
  if (slot) {
    localForm.providerUuid = slot.providerUuid;
    localForm.startDatetime = slot.start ? new Date(slot.start) : null;
    localForm.endDatetime = slot.end ? new Date(slot.end) : null;
    
    noteForm.providerUuid = slot.providerUuid;
    noteForm.start = slot.start ? new Date(slot.start) : null;
    noteForm.end = slot.end ? new Date(slot.end) : null;

    blockForm.providerUuid = slot.providerUuid;
    blockForm.date = slot.start ? new Date(slot.start) : null;
    blockForm.startTime = slot.start ? new Date(slot.start) : null;
    blockForm.endTime = slot.end ? new Date(slot.end) : null;
    
    // Automatically select the day of the week if date is set
    if (blockForm.date) {
      const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
      blockForm.daysOfWeek = [days[blockForm.date.getDay()]];
    }
  }
}, { immediate: true });

const submit = async () => {
  if (activeMainTab.value === 0) {
    await submitBooking();
  } else if (activeMainTab.value === 1) {
    await submitNote();
  } else {
    await submitBlock();
  }
};

const submitBooking = async () => {
  if (!localForm.customerId || !localForm.serviceUuid || !localForm.startDatetime) {
    toast.add({ severity: 'warn', summary: 'Fehlende Daten', detail: 'Bitte wählen Sie einen Kunden, einen Service und eine Startzeit.', life: 3000 });
    return;
  }
  loading.value = true;
  try {
    const isSlotValid = await precheckSlot();
    if (!isSlotValid) {
      loading.value = false;
      return;
    }

    const payload = {
      customer_id: Number(localForm.customerId),
      service_id: localForm.serviceUuid,
      provider_id: localForm.providerUuid,
      start_datetime: formatDate(localForm.startDatetime),
      end_datetime: formatDate(localForm.endDatetime),
      booking_source: 'admin',
      type: 'plan',
      resource_id: localForm.resourceId,
      customer_notes: localForm.customerNotes,
      notes_internal: localForm.notesInternal,
      is_walk_in_booking: localForm.isWalkInBooking,
    };

    await axios.post('/ajax/admin/bookings', payload, { headers: { 'X-Idempotency-Key': window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}` } });

    toast.add({ severity: 'success', summary: 'Erfolg', detail: 'Buchung erfolgreich erstellt.', life: 3000 });
    emit('created');
    visible.value = false;
    resetForms();
  } catch (e) {
    console.error('Create booking failed', e);
    toast.add({ severity: 'error', summary: 'Fehler', detail: extractError(e, 'Buchung konnte nicht erstellt werden.'), life: 5000 });
  } finally {
    loading.value = false;
  }
};

const extractError = (e, defaultMsg = 'Vorgang fehlgeschlagen') => {
    const data = e.response?.data;
    if (data?.errors) {
        const first = Object.values(data.errors).flat()[0];
        if (first) return first;
    }
    return data?.message || e.message || defaultMsg;
};

const quickAddDialogVisible = ref(false);

const onQuickCustomerCreated = (newCustomer) => {
    localForm.customerId = String(newCustomer.id);
    localForm.customerQuery = `${newCustomer.first_name} ${newCustomer.last_name}${newCustomer.email ? ` (${newCustomer.email})` : ''}`;
};

const submitNote = async () => {
  if (!noteForm.note?.trim()) {
    toast.add({ severity: 'warn', summary: 'Fehlende Daten', detail: 'Bitte geben Sie einen Notiztext ein.', life: 3000 });
    return;
  }
  loading.value = true;
  try {
    const payload = {
      note: noteForm.note,
      note_type_id: noteForm.typeUuid,
      provider_id: noteForm.providerUuid,
      start_datetime: formatDate(noteForm.start),
      end_datetime: formatDate(noteForm.end),
      is_time_blocked: noteForm.isBlocked,
    };

    await axios.post('/ajax/admin/notes', payload);

    toast.add({ severity: 'success', summary: 'Erfolg', detail: 'Notiz erfolgreich erstellt.', life: 3000 });
    emit('created');
    visible.value = false;
    resetForms();
  } catch (e) {
    console.error('Create note failed', e);
    toast.add({ severity: 'error', summary: 'Fehler', detail: extractError(e, 'Notiz konnte nicht erstellt werden.'), life: 5000 });
  } finally {
    loading.value = false;
  }
};

const submitBlock = async () => {
  if (!blockForm.title?.trim()) {
    toast.add({ severity: 'warn', summary: 'Fehlende Daten', detail: 'Bitte geben Sie einen Grund ein.', life: 3000 });
    return;
  }
  
  if (blockForm.isRecurring) {
    if (!blockForm.endDate) {
      toast.add({ severity: 'warn', summary: 'Fehlende Daten', detail: 'Bitte wählen Sie ein Enddatum für die Serie.', life: 3000 });
      return;
    }
    if (!blockForm.daysOfWeek.length) {
      toast.add({ severity: 'warn', summary: 'Fehlende Daten', detail: 'Bitte wählen Sie mindestens einen Wochentag.', life: 3000 });
      return;
    }
  }

  loading.value = true;
  try {
    const baseDate = blockForm.date instanceof Date ? blockForm.date : new Date(blockForm.date);
    const startObj = blockForm.startTime instanceof Date ? blockForm.startTime : new Date(blockForm.startTime);
    const endObj = blockForm.endTime instanceof Date ? blockForm.endTime : new Date(blockForm.endTime);
    
    const finalStart = new Date(baseDate);
    finalStart.setHours(startObj.getHours(), startObj.getMinutes(), 0, 0);

    const finalEnd = new Date(baseDate);
    finalEnd.setHours(endObj.getHours(), endObj.getMinutes(), 0, 0);

    let payload = null;
    let endpoint = '';

    if (blockForm.isRecurring) {
      const eDate = blockForm.endDate instanceof Date ? blockForm.endDate : new Date(blockForm.endDate);
      payload = {
        title: blockForm.title,
        notes: blockForm.notes,
        provider_id: blockForm.providerUuid,
        start_date: formatDate(baseDate).split(' ')[0],
        end_date: formatDate(eDate).split(' ')[0],
        start_time: startObj.toTimeString().substring(0, 5),
        end_time: endObj.toTimeString().substring(0, 5),
        days_of_week: blockForm.daysOfWeek,
      };
      endpoint = '/management/provider-block-series';
    } else {
      payload = {
        title: blockForm.title,
        notes: blockForm.notes,
        provider_id: blockForm.providerUuid,
        date: formatDate(baseDate).split(' ')[0],
        start_datetime: formatDate(finalStart),
        end_datetime: formatDate(finalEnd),
      };
      endpoint = '/management/provider-blocks';
    }

    await axios.post(endpoint, payload);

    toast.add({ severity: 'success', summary: 'Erfolg', detail: blockForm.isRecurring ? 'Blockierungsserie erfolgreich erstellt.' : 'Blockierte Zeit erfolgreich erstellt.', life: 3000 });
    emit('created');
    visible.value = false;
    resetForms();
  } catch (e) {
    console.error('Create block failed', e);
    toast.add({ severity: 'error', summary: 'Fehler', detail: extractError(e, 'Blockierte Zeit konnte nicht erstellt werden.'), life: 5000 });
  } finally {
    loading.value = false;
  }
};

</script>

<template>
  <Dialog 
    v-model:visible="visible" 
    :modal="true" 
    :style="{ width: '800px' }" 
    :pt="{
      header: { class: '!p-5 !border-b !border-gray-100 !items-center' }
    }"
  >
    <template #header>
      <div class="flex items-center gap-3 w-full">
        <button 
          @click="activeMainTab = 0"
          :class="[
            'px-6 py-2.5 rounded-full font-bold transition-all text-sm uppercase tracking-wider', 
            activeMainTab === 0 ? 'bg-black text-[#FFF053] shadow-lg' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
          ]"
        >
          Buchung
        </button>
        <button 
          @click="activeMainTab = 1"
          :class="[
            'px-6 py-2.5 rounded-full font-bold transition-all text-sm uppercase tracking-wider', 
            activeMainTab === 1 ? 'bg-black text-[#FFF053] shadow-lg' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
          ]"
        >
          Notiz
        </button>
        <button 
          @click="activeMainTab = 2"
          :class="[
            'px-6 py-2.5 rounded-full font-bold transition-all text-sm uppercase tracking-wider', 
            activeMainTab === 2 ? 'bg-black text-[#FFF053] shadow-lg' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
          ]"
        >
          Blockieren
        </button>
      </div>
    </template>

    <div class="mt-2">
      <!-- FORM 1: CREATE BOOKING -->
      <div v-if="activeMainTab === 0" class="animate-in fade-in duration-300">
        <div class="space-y-3 pt-2 px-4">
          <!-- Client Row -->
          <div class="grid grid-cols-12 gap-4 items-center">
            <label class="col-span-4 font-bold text-gray-600">Kunde</label>
            <div class="col-span-8 flex gap-2">
              <div class="relative flex-1">
                <i class="pi pi-search absolute left-3 top-1/2 -translate-y-1/2 z-10 text-gray-400"></i>
                <InputText 
                  v-model="localForm.customerQuery" 
                  @input="searchCustomers"
                  class="w-full !pl-10 !rounded-lg" 
                  placeholder="Suchen..." 
                  autofocus
                />
                <!-- Search Results Overlay -->
                <div v-if="customerOptions.length" class="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-xl max-h-48 overflow-y-auto">
                  <button 
                    v-for="option in customerOptions" 
                    :key="option.id" 
                    @click="chooseCustomer(option)"
                    class="w-full text-left px-4 py-2 hover:bg-gray-100 text-sm border-b last:border-b-0"
                  >
                    {{ option.label }}
                  </button>
                </div>
              </div>
              <Button 
                icon="pi pi-plus" 
                class="!bg-gray-100 !text-gray-600 !border-gray-200 flex-shrink-0" 
                v-tooltip="'Laufkunde schnell anlegen'" 
                @click="quickAddDialogVisible = true"
              />
            </div>
          </div>

          <!-- Category Row -->
          <!-- <div class="grid grid-cols-12 gap-4 items-center">
            <label class="col-span-4 font-bold text-gray-600">Service-Kategorie</label>
            <div class="col-span-8">
              <select class="w-full p-2 border border-gray-300 rounded-lg bg-gray-50 focus:ring-2 focus:ring-black outline-none">
                <option value="">Bitte wählen...</option>
                <option v-for="c in serviceCategories" :key="c.uuid" :value="c.uuid">{{ c.name }}</option>
              </select>
            </div>
          </div> -->

          <!-- Provider Row -->
          <div class="grid grid-cols-12 gap-4 items-center">
            <label class="col-span-4 font-bold text-gray-600">Mitarbeiter *</label>
            <div class="col-span-8">
              <Dropdown 
                v-model="localForm.providerUuid" 
                :options="allProviders" 
                optionLabel="name" 
                optionValue="uuid" 
                placeholder="Mitarbeiter wählen"
                class="w-full !rounded-lg"
              />
            </div>
          </div>

          <!-- Service Row -->
          <div class="grid grid-cols-12 gap-4 items-center">
            <label class="col-span-4 font-bold text-gray-600">Service *</label>
            <div class="col-span-8">
              <Dropdown 
                v-model="localForm.serviceUuid" 
                :options="serviceOptionsLocal" 
                optionLabel="name" 
                optionValue="uuid" 
                placeholder="Service wählen"
                class="w-full !rounded-lg"
              />
            </div>
          </div>



          <!-- Time Row (Start & End) -->
          <div class="grid grid-cols-12 gap-4 items-start">
            <label class="col-span-4 font-bold text-gray-600 pt-2">Start / Ende *</label>
            <div class="col-span-8 space-y-2">
              <div class="flex gap-3 items-center">
                <div class="flex-1">
                  <Calendar 
                    v-model="localForm.startDatetime" 
                    showTime 
                    :stepMinute="15"
                    hourFormat="24" 
                    dateFormat="dd.mm.yy" 
                    class="w-full"
                    inputClass="p-2 border border-gray-300 rounded-lg bg-gray-50 text-indigo-600 font-semibold focus:ring-2 focus:ring-black outline-none"
                  />
                </div>
                <i class="pi pi-arrow-right text-gray-300 text-xs"></i>
                <div class="flex-1">
                  <Calendar 
                    v-model="localForm.endDatetime" 
                    showTime 
                    :stepMinute="15"
                    hourFormat="24" 
                    dateFormat="dd.mm.yy" 
                    class="w-full"
                    inputClass="p-2 border border-gray-300 rounded-lg bg-white text-indigo-600 font-semibold focus:ring-2 focus:ring-black outline-none"
                    placeholder="Service-Ende"
                  />
                </div>
              </div>
              <!-- Duration breakdown chips -->
              <div v-if="durationBreakdown" class="flex flex-wrap items-center gap-1.5 pt-1">
                <template v-for="(part, i) in durationBreakdown.parts" :key="i">
                  <span 
                    class="text-[10px] font-bold px-2 py-0.5 rounded-full"
                    :class="part.includes('Service') ? 'bg-indigo-50 text-indigo-600 border border-indigo-100' : 'bg-orange-50 text-orange-500 border border-orange-100'"
                  >{{ part }}</span>
                  <span v-if="i < durationBreakdown.parts.length - 1" class="text-gray-300 text-[10px] font-bold">+</span>
                </template>
                <span class="text-[10px] font-black text-gray-500 ml-1">= {{ durationBreakdown.total }}m gesamt</span>
              </div>
              <!-- Blocked window row (only shown when buffer_before > 0) -->
              <div v-if="durationBreakdown && selectedService?.buffer_before > 0" class="flex items-center gap-2 pt-0.5">
                <i class="pi pi-lock text-[9px] text-gray-400"></i>
                <span class="text-[10px] text-gray-400 font-bold">Blockiert:</span>
                <span class="text-[10px] font-black text-gray-600">
                  {{ formatTime(blockedStartDatetime) }} → {{ calculatedEndDatetime ? formatTime(new Date(calculatedEndDatetime)) : '--:--' }}
                </span>
                <span class="text-[9px] text-gray-400">({{ durationBreakdown.total }}m gesamt)</span>
              </div>
            </div>
          </div>

          <!-- Device Resource Row (if allowed) -->
          <div v-if="hasDeviceSelection" class="grid grid-cols-12 gap-4 items-center pt-2">
            <label class="col-span-4 font-bold text-gray-600">Gerät wählen</label>
            <div class="col-span-8">
              <Dropdown 
                v-model="localForm.resourceId" 
                :options="availableResources" 
                optionLabel="name" 
                optionValue="resource_id" 
                :optionDisabled="option => !option.available"
                placeholder="Verfügbares Gerät wählen"
                class="w-full !rounded-lg"
                :loading="loadingResources"
                emptyMessage="Keine Geräte verfügbar"
              >
                <template #option="slotProps">
                   <div class="flex items-center justify-between w-full">
                      <span>{{ slotProps.option.name }}</span>
                      <span v-if="!slotProps.option.available" class="text-xs text-red-500 font-bold ml-2">(Belegt)</span>
                   </div>
                </template>
              </Dropdown>
            </div>
          </div>

          <!-- Internal Notes (Optional) -->
          <div class="grid grid-cols-12 gap-4 pt-4 border-t">
            <label class="col-span-4 font-bold text-gray-600">Notizen</label>
            <div class="col-span-8 space-y-2">
              <Textarea v-model="localForm.customerNotes" rows="2" class="w-full !rounded-lg text-sm" placeholder="Kunden-Notiz..." autoResize />
              <Textarea v-model="localForm.notesInternal" rows="2" class="w-full !rounded-lg text-sm" placeholder="Interne Notiz..." autoResize />
            </div>
          </div>

          <!-- Walk-in Booking Bypass Toggle -->
          <div class="grid grid-cols-12 gap-4 pt-4 border-t items-center">
            <label class="col-span-4 font-bold text-gray-600">Laufkunde</label>
            <div class="col-span-8">
              <div class="flex items-center gap-2">
                <Checkbox v-model="localForm.isWalkInBooking" inputId="walkInToggle" :binary="true" />
                <label for="walkInToggle" class="text-sm text-gray-600 cursor-pointer font-bold">
                  Walk-In Booking (Kontingentprüfung ignorieren)
                </label>
              </div>
              <p class="text-[11px] text-gray-400 mt-1 leading-tight">
                Wenn aktiviert, wird die Paket- und Planprüfung übersprungen.
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- FORM 2: CREATE NOTE -->
      <div v-if="activeMainTab === 1" class="animate-in fade-in duration-300">
        <div class="space-y-3 pt-2">
          
          <!-- Start/End Row -->
          <div class="grid grid-cols-12 gap-4 items-center">
            <label class="col-span-4 font-bold text-gray-600 text-sm">Start / Ende *</label>
            <div class="col-span-8 flex gap-3 items-center">
              <div class="flex-1">
                <Calendar 
                  v-model="noteForm.start" 
                  showTime 
                  :stepMinute="15"
                  hourFormat="24" 
                  dateFormat="dd.mm.yy"
                  class="w-full"
                  inputClass="p-2 border border-gray-300 rounded-lg bg-gray-50 text-indigo-600 font-semibold focus:ring-2 focus:ring-black outline-none text-sm"
                />
              </div>
              <i class="pi pi-arrow-right text-gray-300 text-xs"></i>
              <div class="flex-1">
                <Calendar 
                  v-model="noteForm.end" 
                  showTime 
                  :stepMinute="15"
                  hourFormat="24" 
                  dateFormat="dd.mm.yy"
                  class="w-full"
                  inputClass="p-2 border border-gray-300 rounded-lg bg-gray-50 text-indigo-600 font-semibold focus:ring-2 focus:ring-black outline-none text-sm"
                />
              </div>
            </div>
          </div>

          <!-- Time is blocked -->
          <div class="grid grid-cols-12 gap-4 items-center">
            <label class="col-span-4 font-bold text-gray-600 text-sm">Zeit blockieren</label>
            <div class="col-span-8">
               <Checkbox 
                  id="is_blocked_note" 
                  v-model="noteForm.isBlocked" 
                  :binary="true" 
                  class="cursor-pointer"
                />
            </div>
          </div>

          <!-- Note Textarea -->
          <div class="grid grid-cols-12 gap-4">
            <label class="col-span-4 font-bold text-gray-600 text-sm pt-2">Notiz</label>
            <div class="col-span-8">
              <Textarea 
                v-model="noteForm.note" 
                rows="4" 
                class="w-full !p-3 !rounded-xl !bg-gray-50 focus:!ring-2 focus:!ring-black outline-none transition-all text-sm"
                placeholder="Geben Sie hier Ihre Notiz ein..."
                autoResize
              />
            </div>
          </div>

          <!-- Provider Row -->
          <div class="grid grid-cols-12 gap-4 items-center">
            <label class="col-span-4 font-bold text-gray-600 text-sm">Mitarbeiter</label>
            <div class="col-span-8">
              <Dropdown 
                v-model="noteForm.providerUuid" 
                :options="allProviders" 
                optionLabel="name" 
                optionValue="uuid" 
                placeholder="Mitarbeiter wählen"
                class="w-full !rounded-lg"
              />
            </div>
          </div>

          <!-- Note Type Row -->
          <div class="grid grid-cols-12 gap-4 items-center border-t pt-4">
            <label class="col-span-4 font-bold text-gray-600 text-sm">Notiz-Typ *</label>
            <div class="col-span-8 flex gap-2 items-center">
              <Dropdown 
                v-model="noteForm.typeUuid" 
                :options="noteTypes" 
                optionLabel="name" 
                optionValue="uuid" 
                placeholder="Typ wählen"
                class="flex-1 !rounded-lg"
              >
                <template #value="slotProps">
                  <div v-if="slotProps.value" class="flex items-center gap-2">
                    <div 
                      class="w-3.5 h-3.5 rounded-full border border-gray-100" 
                      :style="{ backgroundColor: noteTypes.find(t => t.uuid === slotProps.value)?.color || '#ddd' }"
                    ></div>
                    <span>{{ noteTypes.find(t => t.uuid === slotProps.value)?.name }}</span>
                  </div>
                  <span v-else>{{ slotProps.placeholder }}</span>
                </template>
                <template #option="slotProps">
                  <div class="flex items-center gap-2">
                    <div 
                      class="w-3.5 h-3.5 rounded-full border border-gray-100" 
                      :style="{ backgroundColor: slotProps.option.color || '#ddd' }"
                    ></div>
                    <span>{{ slotProps.option.name }}</span>
                  </div>
                </template>
              </Dropdown>
              <!-- <Button icon="pi pi-pencil" class="!bg-gray-100 !text-gray-600 !border-gray-200 !rounded-lg !w-10 !h-10 flex items-center justify-center" /> -->
            </div>
          </div>
        </div>
      </div>

      <!-- FORM 3: CREATE PROVIDER BLOCK -->
      <div v-if="activeMainTab === 2" class="animate-in fade-in duration-300">
        <div class="space-y-3 pt-2">
          
          <!-- Recurring Toggle -->
          <div class="grid grid-cols-12 gap-4 items-center">
            <label class="col-span-4 font-bold text-gray-600 text-sm">Wiederholen</label>
            <div class="col-span-8">
              <Checkbox 
                id="is_recurring_block" 
                v-model="blockForm.isRecurring" 
                :binary="true" 
                class="cursor-pointer"
              />
            </div>
          </div>

          <!-- Date Fields (Based on Recurrence) -->
          <div v-if="!blockForm.isRecurring" class="grid grid-cols-12 gap-4 items-center">
            <label class="col-span-4 font-bold text-gray-600 text-sm">Datum *</label>
            <div class="col-span-8 flex gap-3 items-center">
              <div class="flex-1">
                <Calendar 
                  v-model="blockForm.date" 
                  dateFormat="dd.mm.yy"
                  class="w-full"
                  inputClass="p-2 border border-gray-300 rounded-lg bg-gray-50 text-indigo-600 font-semibold focus:ring-2 focus:ring-black outline-none text-sm w-full"
                />
              </div>
              <i class="pi pi-arrow-right text-transparent text-xs"></i>
              <div class="flex-1"></div>
            </div>
          </div>
          
          <div v-else class="animate-in fade-in duration-300 space-y-3">
            <div class="grid grid-cols-12 gap-4 items-center">
              <label class="col-span-4 font-bold text-gray-600 text-sm">Start / Ende *</label>
              <div class="col-span-8 flex gap-3 items-center">
                <div class="flex-1">
                  <Calendar 
                    v-model="blockForm.date" 
                    dateFormat="dd.mm.yy"
                    class="w-full"
                    inputClass="p-2 border border-gray-300 rounded-lg bg-gray-50 text-indigo-600 font-semibold focus:ring-2 focus:ring-black outline-none text-sm w-full"
                  />
                </div>
                <i class="pi pi-arrow-right text-gray-300 text-xs"></i>
                <div class="flex-1">
                  <Calendar 
                    v-model="blockForm.endDate" 
                    dateFormat="dd.mm.yy"
                    class="w-full"
                    inputClass="p-2 border border-gray-300 rounded-lg bg-gray-50 text-indigo-600 font-semibold focus:ring-2 focus:ring-black outline-none text-sm w-full"
                  />
                </div>
              </div>
            </div>

            <div class="grid grid-cols-12 gap-4 items-center">
              <label class="col-span-4 font-bold text-gray-600 text-sm">Wochentage *</label>
              <div class="col-span-8 flex gap-1">
                <div 
                  v-for="day in [
                    { val: 'monday', label: 'Mo' },
                    { val: 'tuesday', label: 'Di' },
                    { val: 'wednesday', label: 'Mi' },
                    { val: 'thursday', label: 'Do' },
                    { val: 'friday', label: 'Fr' },
                    { val: 'saturday', label: 'Sa' },
                    { val: 'sunday', label: 'So' }
                  ]" 
                  :key="day.val"
                  @click="blockForm.daysOfWeek.includes(day.val) ? blockForm.daysOfWeek = blockForm.daysOfWeek.filter(d => d !== day.val) : blockForm.daysOfWeek.push(day.val)"
                  :class="[
                    'w-8 h-8 rounded-full flex items-center justify-center text-[11px] font-bold cursor-pointer transition-all',
                    blockForm.daysOfWeek.includes(day.val) 
                      ? 'bg-black text-[#FFF053] shadow-md' 
                      : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                  ]"
                >
                  {{ day.label }}
                </div>
              </div>
            </div>
          </div>

          <!-- Time Row -->
          <div class="grid grid-cols-12 gap-4 items-center">
            <label class="col-span-4 font-bold text-gray-600 text-sm">Start / Ende *</label>
            <div class="col-span-8 flex gap-3 items-center">
              <div class="flex-1">
                <Calendar 
                  v-model="blockForm.startTime" 
                  timeOnly 
                  :stepMinute="15"
                  class="w-full"
                  inputClass="p-2 border border-gray-300 rounded-lg bg-gray-50 text-indigo-600 font-semibold focus:ring-2 focus:ring-black outline-none text-sm w-full"
                />
              </div>
              <i class="pi pi-arrow-right text-gray-300 text-xs"></i>
              <div class="flex-1">
                <Calendar 
                  v-model="blockForm.endTime" 
                  timeOnly 
                  :stepMinute="15"
                  class="w-full"
                  inputClass="p-2 border border-gray-300 rounded-lg bg-gray-50 text-indigo-600 font-semibold focus:ring-2 focus:ring-black outline-none text-sm w-full"
                />
              </div>
            </div>
          </div>

          <!-- Title Input -->
          <div class="grid grid-cols-12 gap-4 items-center">
            <label class="col-span-4 font-bold text-gray-600 text-sm pt-2">Grund *</label>
            <div class="col-span-8">
              <InputText 
                v-model="blockForm.title" 
                class="w-full !rounded-lg border-gray-300 focus:!ring-2 focus:!ring-black outline-none text-sm"
                placeholder="z.B. Mittagspause, Urlaub..."
              />
            </div>
          </div>

          <!-- Note Textarea -->
          <div class="grid grid-cols-12 gap-4">
            <label class="col-span-4 font-bold text-gray-600 text-sm pt-2">Notiz</label>
            <div class="col-span-8">
              <Textarea 
                v-model="blockForm.notes" 
                rows="4" 
                class="w-full !p-3 !rounded-xl !bg-gray-50 focus:!ring-2 focus:!ring-black outline-none transition-all text-sm"
                placeholder="Optionale Details..."
                autoResize
              />
            </div>
          </div>

          <!-- Provider Row -->
          <div class="grid grid-cols-12 gap-4 items-center">
            <label class="col-span-4 font-bold text-gray-600 text-sm">Mitarbeiter</label>
            <div class="col-span-8">
              <Dropdown 
                v-model="blockForm.providerUuid" 
                :options="allProviders" 
                optionLabel="name" 
                optionValue="uuid" 
                placeholder="Mitarbeiter wählen"
                class="w-full !rounded-lg"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <template #footer>
      <div class="flex justify-between items-center w-full pt-4 border-t mt-4">
        <button 
          @click="visible = false" 
          class="text-gray-500 font-bold hover:!bg-sky-50 hover:!text-sky-600 transition-all px-6 py-2 rounded-full text-sm"
        >
          Abbrechen
        </button>
        <Button 
          :loading="loading" 
          class="!bg-black !text-[#FFF053] !border-none px-8 py-3 rounded-xl font-bold shadow-lg flex items-center gap-3" 
          @click="submit" 
        >
          <i class="pi pi-save text-lg"></i>
          <span class="uppercase tracking-widest text-sm">Speichern</span>
        </Button>
      </div>
    </template>
  </Dialog>

  <QuickAddCustomerModal 
    v-model="quickAddDialogVisible" 
    @created="onQuickCustomerCreated" 
  />
</template>
